import type { HubspotContact, HubspotPro } from "@optee/models";
import type { OperationHubspotPrestationId } from "./operation.constant";
import { getPrestationsParentCategories } from "./operation.constant";
import type { YouSignLocation } from "./yousign.constant";

export const Departements = [
  "1 - Ain",
  "2 - Aisne",
  "3 - Allier",
  "4 - Alpes de Haute-Provence",
  "5 - Hautes-Alpes",
  "6 - Alpes-Maritimes",
  "7 - Ardêche",
  "8 - Ardennes",
  "9 - Ariège",
  "10 - Aube",
  "11 - Aude",
  "12 - Aveyron",
  "13 - Bouches-du-Rhône",
  "14 - Calvados",
  "15 - Cantal",
  "16 - Charente",
  "17 - Charente-Maritime",
  "18 - Cher",
  "19 - Corrèze",
  "2A - Corse-du-Sud",
  "2B - Haute-Corse",
  "21 - Côte-d'Or",
  "22 - Côtes d'Armor",
  "23 - Creuse",
  "24 - Dordogne",
  "25 - Doubs",
  "26 - Drôme",
  "27 - Eure",
  "28 - Eure-et-Loir",
  "29 - Finistère",
  "30 - Gard",
  "31 - Haute-Garonne",
  "32 - Gers",
  "33 - Gironde",
  "34 - Hérault",
  "35 - Îlle-et-Vilaine",
  "36 - Indre",
  "37 - Indre-et-Loire",
  "38 - Isère",
  "39 - Jura",
  "40 - Landes",
  "41 - Loir-et-Cher",
  "42 - Loire",
  "43 - Haute-Loire",
  "44 - Loire-Atlantique",
  "45 - Loiret",
  "46 - Lot",
  "47 - Lot-et-Garonne",
  "48 - Lozère",
  "49 - Maine-et-Loire",
  "50 - Manche",
  "51 - Marne",
  "52 - Haute-Marne",
  "53 - Mayenne",
  "54 - Meurthe-et-Moselle",
  "55 - Meuse",
  "56 - Morbihan",
  "57 - Moselle",
  "58 - Nièvre",
  "59 - Nord",
  "60 - Oise",
  "61 - Orne",
  "62 - Pas-de-Calais",
  "63 - Puy-de-Dôme",
  "64 - Pyrénées-Atlantiques",
  "65 - Hautes-Pyrénées",
  "66 - Pyrénées-Orientales",
  "67 - Bas-Rhin",
  "68 - Haut-Rhin",
  "69 - Rhône",
  "70 - Haute-Saône",
  "71 - Saône-et-Loire",
  "72 - Sarthe",
  "73 - Savoie",
  "74 - Haute-Savoie",
  "75 - Paris",
  "76 - Seine-Maritime",
  "77 - Seine-et-Marne",
  "78 - Yvelines",
  "79 - Deux-Sèvres",
  "80 - Somme",
  "81 - Tarn",
  "82 - Tarn-et-Garonne",
  "83 - Var",
  "84 - Vaucluse",
  "85 - Vendée",
  "86 - Vienne",
  "87 - Haute-Vienne",
  "88 - Vosges",
  "89 - Yonne",
  "90 - Territoire-de-Belfort",
  "91 - Essonne",
  "92 - Hauts-de-Seine",
  "93 - Seine-Saint-Denis",
  "94 - Val-de-Marne",
  "95 - Val-d'Oise",
] as const;

export type Departement = (typeof Departements)[number];

export const PRO_FILES_METADATA = [
  {
    label: "rib",
    title: "Relevé d’identité bancaire",
    subtitle: null,
    required: true,
    multiple: false,
    hasModel: false,
  },
  {
    label: "kbis",
    title: "Kbis de moins de 3 mois",
    subtitle: null,
    required: true,
    multiple: false,
    hasModel: false,
  },
  {
    label: "statuts",
    title: "Statuts de l'entreprise",
    subtitle: "Les statuts doivent obligatoirement être signés",
    required: true,
    multiple: false,
    hasModel: false,
  },
  {
    label: "id-dirigeant",
    title: "Document d’identité du/des gérant(s)",
    subtitle:
      "Pour toutes les personnes possédant +25% des parts de l’entreprise",
    required: true,
    multiple: false,
    hasModel: false,
  },
  {
    label: "dbe-s-1",
    title: "Déclaration bénéficiaire effectif (DBE-S-1)",
    subtitle: "Document relatif au bénéficiaire effectif d’une société",
    required: true,
    multiple: false,
    hasModel: true,
  },
  {
    label: "dbe-s-2",
    title: "Déclaration annexe bénéficiaire effectif (DBE-S-2) (facultatif)",
    subtitle: "Autre bénéficiaire possédant au moins 25% de l’entreprise",
    required: false,
    multiple: false,
    hasModel: true,
  },
  {
    label: "assurances",
    title: "Assurances décennales (facultatif)",
    subtitle: null,
    required: false,
    multiple: false,
    hasModel: false,
  },
  {
    label: "autres",
    title: "Autres documents (facultatif)",
    subtitle: "Documents spécifiques à la catégorie de travaux choisie",
    required: false,
    multiple: true,
    hasModel: false,
  },
] as const;

export function determineDocumentType(
  fileName: string,
): ProFileMetadataLabel | null {
  if (fileName.startsWith("Autres - ")) {
    return "autres";
  }

  // Otherwise, find the metadata that has this label
  const found = PRO_FILES_METADATA.find((meta) => meta.label === fileName);
  return found?.label ?? null;
}

export const PRO_WITH_CEE_FILES_METADATA = [
  ...PRO_FILES_METADATA,
  {
    label: "rge",
    title: "Certifications RGE",
    subtitle: "Certificats liés aux opérations que vous prenez en charge",
    required: true,
    multiple: false,
    hasModel: false,
  },
] as const;
export type ProFileMetadata = (typeof PRO_WITH_CEE_FILES_METADATA)[number];

export type ProFileMetadataLabel = ProFileMetadata["label"];
export const PRO_FILES_METADATA_LABELS = PRO_WITH_CEE_FILES_METADATA.map(
  (metadata) => metadata.label,
) as [ProFileMetadataLabel, ...ProFileMetadataLabel[]];

export const REQUIRED_FILES_LABELS = PRO_WITH_CEE_FILES_METADATA.filter(
  (metadata) => metadata.required,
).map((metadata) => metadata.label) as [
  ProFileMetadataLabel,
  ...ProFileMetadataLabel[],
];

export const PRO_STATUSES = [
  "Invitation envoyée",
  "Onboarding plateforme",
  "En attente de signature plateforme",
  "Compte en attente de validation",
  "Actif",
  "Inactif",
  "Out",
] as const;
export type ProStatus = (typeof PRO_STATUSES)[number];

export const CONTRACT_DIRECTORY = "assets/pdf/contracts";

export const PRO_CONTRACTS = [
  {
    documentName: "Contrat de Partenariat - Certificat d'économie d'énergie",
    cee: true,
    id: "partnership",
    file: "partnership-cee.pdf",
    signaturePosition: {
      page: 13,
      width: 251,
      height: 70,
      x: 64,
      y: 551,
    },
    fields(
      pro: HubspotPro,
      contact: HubspotContact,
    ): {
      text: string | null;
      position: YouSignLocation;
    }[] {
      return [
        {
          text: pro.name,
          position: {
            page: 1,
            width: 363,
            height: 24,
            x: 165,
            y: 351,
          },
        },
        {
          text: pro.rcsLocation,
          position: {
            page: 1,
            width: 310,
            height: 24,
            x: 232,
            y: 381,
          },
        },
        {
          text: pro.siren,
          position: {
            page: 1,
            width: 370,
            height: 24,
            x: 157,
            y: 409,
          },
        },
        {
          text: `${pro.street} ${pro.zipcode} ${pro.city}`,
          position: {
            page: 1,
            width: 340,
            height: 24,
            x: 250,
            y: 438,
          },
        },
        {
          text: `${contact.firstName} ${contact.lastName}`,
          position: {
            page: 1,
            width: 283,
            height: 24,
            x: 300,
            y: 467,
          },
        },
        {
          text: contact.jobTitle,
          position: {
            page: 1,
            width: 246,
            height: 24,
            x: 190,
            y: 495,
          },
        },
        {
          text: contact.email,
          position: {
            page: 1,
            width: 386,
            height: 24,
            x: 142,
            y: 524,
          },
        },
        {
          text: getPrestationsParentCategories(
            (pro.prestations?.split(";") ??
              []) as OperationHubspotPrestationId[],
          ).join(", "),
          position: {
            page: 2,
            width: 456,
            height: 50,
            x: 67,
            y: 290,
          },
        },

        {
          text: "Lu et approuvé le " + new Date().toLocaleDateString("fr-FR"),
          position: {
            page: 13,
            width: 251,
            height: 24,
            x: 122,
            y: 414,
          },
        },
        {
          text: pro.name,
          position: {
            page: 13,
            width: 251,
            height: 24,
            x: 96,
            y: 477,
          },
        },
        {
          text: `${contact.firstName} ${contact.lastName}`,
          position: {
            page: 13,
            width: 251,
            height: 24,
            x: 63,
            y: 514,
          },
        },
      ];
    },
  },
  {
    documentName: "Contrat de Partenariat",
    cee: false,
    id: "partnership",
    file: "partnership.pdf",
    signaturePosition: {
      page: 12,
      width: 190,
      height: 72,
      x: 63,
      y: 732,
    },
    fields(
      pro: HubspotPro,
      contact: HubspotContact,
    ): {
      text: string | null;
      position: YouSignLocation;
    }[] {
      return [
        {
          text: pro.name,
          position: {
            page: 1,
            width: 355,
            height: 24,
            x: 167,
            y: 351,
          },
        },
        {
          text: pro.rcsLocation,
          position: {
            page: 1,
            width: 294,
            height: 24,
            x: 234,
            y: 379,
          },
        },

        {
          text: pro.siren,
          position: {
            page: 1,
            width: 368,
            height: 24,
            x: 158,
            y: 410,
          },
        },
        {
          text: `${pro.street} ${pro.zipcode} ${pro.city}`,
          position: {
            page: 1,
            width: 300,
            height: 24,
            x: 252,
            y: 437,
          },
        },
        {
          text: `${contact.firstName} ${contact.lastName}`,
          position: {
            page: 1,
            width: 230,
            height: 24,
            x: 300,
            y: 466,
          },
        },
        {
          text: contact.jobTitle,
          position: {
            page: 1,
            width: 240,
            height: 24,
            x: 190,
            y: 495,
          },
        },
        {
          text: contact.email,
          position: {
            page: 1,
            width: 383,
            height: 24,
            x: 144,
            y: 524,
          },
        },
        {
          text: getPrestationsParentCategories(
            (pro.prestations?.split(";") ??
              []) as OperationHubspotPrestationId[],
          ).join(", "),
          position: {
            page: 2,
            width: 453,
            height: 24,
            x: 72,
            y: 290,
          },
        },

        {
          text: "Lu et approuvé le " + new Date().toLocaleDateString("fr-FR"),
          position: {
            page: 12,
            width: 190,
            height: 24,
            x: 120,
            y: 576,
          },
        },
        {
          text: pro.name,
          position: {
            page: 12,
            width: 190,
            height: 24,
            x: 94,
            y: 651,
          },
        },
        {
          text: `${contact.firstName} ${contact.lastName}`,
          position: {
            page: 12,
            width: 190,
            height: 24,
            x: 63,
            y: 685,
          },
        },
      ];
    },
  },
  {
    documentName: "Contrat de valorisation - Certificat d'économie d'énergie",
    cee: true,
    id: "cee",
    file: "cee.pdf",
    signaturePosition: {
      page: 29,
      width: 192,
      height: 50,
      x: 70,
      y: 500,
    },
    fields(
      pro: HubspotPro,
      contact: HubspotContact,
    ): {
      text: string | null;
      position: YouSignLocation;
    }[] {
      return [
        {
          text: pro.name,
          position: {
            page: 1,
            width: 319,
            height: 24,
            x: 133,
            y: 372,
          },
        },
        {
          text: pro.capital?.toFixed(0) ?? null,
          position: {
            page: 1,
            width: 103,
            height: 24,
            x: 140,
            y: 398,
          },
        },
        {
          text: `${pro.street} ${pro.zipcode} ${pro.city}`,
          position: {
            page: 1,
            width: 308,
            height: 24,
            x: 222,
            y: 425,
          },
        },
        {
          text: pro.rcsLocation,
          position: {
            page: 1,
            width: 219,
            height: 24,
            x: 373,
            y: 453,
          },
        },
        {
          text: pro.siren,
          position: {
            page: 1,
            width: 392,
            height: 24,
            x: 154,
            y: 481,
          },
        },
        {
          text: `${contact.firstName} ${contact.lastName}`,
          position: {
            page: 1,
            width: 392,
            height: 24,
            x: 159,
            y: 509,
          },
        },
        {
          text: contact.jobTitle,
          position: {
            page: 1,
            width: 392,
            height: 24,
            x: 141,
            y: 536,
          },
        },
        {
          text: "Lu et approuvé le " + new Date().toLocaleDateString("fr-FR"),
          position: {
            page: 29,
            width: 192,
            height: 24,
            x: 70,
            y: 430,
          },
        },
        {
          text: contact.jobTitle,
          position: {
            page: 29,
            width: 192,
            height: 24,
            x: 117,
            y: 469,
          },
        },
      ];
    },
  },
] as const;
export const ContractTypes = PRO_CONTRACTS.map((contract) => contract.id) as [
  ContractType,
  ...ContractType[],
];
export type ContractToSign = (typeof PRO_CONTRACTS)[number];
export type ContractType = (typeof PRO_CONTRACTS)[number]["id"];

export const getContractFromType = (type: ContractType, cee: boolean) =>
  PRO_CONTRACTS.find(
    (contract) => contract.id === type && cee === contract.cee,
  );
